import type {
  StaffImportCandidate,
  StaffImportRecordDecisionBody,
  StaffImportRequiredDecision,
  StaffImportReviewRowDetail,
  StaffImportRowDecision,
} from "@lafayette/schemas";
import { targetFieldLabel } from "./format";
import { decisionKindLabels } from "./labels";

// The decision rules of the review drawer, as pure functions of the R6 answer and of what the staff
// member chose on the page but has not saved yet (the "drafts"). The server owns the truth: it says
// which decisions a row owes (`requiredDecisions`) and which are resolved. This file decides what the
// page offers and what it blocks. Every reason is a sentence built from a code, never from a cell.

/** The keep-as-text choice of a CURRENCY decision (`IMPORT_KEEP_AS_TEXT` in the domain). */
export const KEEP_AS_TEXT = "KEEP_AS_TEXT";

export type DecisionKey = string;

export const decisionKey = (
  decision: Pick<StaffImportRequiredDecision, "kind" | "fieldCode">,
): DecisionKey => `${decision.kind}:${decision.fieldCode ?? ""}`;

/** What staff chose for one decision. The fields follow `StaffImportDecisionBody`. */
export type DecisionDraft = Readonly<{
  chosenCode: string | null;
  chosenCatalogItemId: string | null;
  confirmedChangeSha256: string | null;
  assistId: string | null;
}>;

export const emptyDraft: DecisionDraft = {
  chosenCode: null,
  chosenCatalogItemId: null,
  confirmedChangeSha256: null,
  assistId: null,
};

/** The drafts of one row, by decision key. */
export type RowDrafts = Readonly<Record<DecisionKey, DecisionDraft>>;

/** The latest decision for each `(kind, fieldCode)`. A later decision replaces an earlier one. */
export function latestDecisions(
  decisions: readonly StaffImportRowDecision[],
): ReadonlyMap<DecisionKey, StaffImportRowDecision> {
  const latest = new Map<DecisionKey, StaffImportRowDecision>();
  for (const decision of decisions) {
    const key = decisionKey(decision);
    const current = latest.get(key);
    if (current === undefined || decision.createdAt.getTime() >= current.createdAt.getTime()) {
      latest.set(key, decision);
    }
  }
  return latest;
}

export const draftOfDecision = (decision: StaffImportRowDecision): DecisionDraft => ({
  chosenCode: decision.chosenCode,
  chosenCatalogItemId: decision.chosenCatalogItemId,
  confirmedChangeSha256: decision.confirmedChangeSha256,
  assistId: decision.assistId,
});

export const sameDraft = (a: DecisionDraft, b: DecisionDraft): boolean =>
  a.chosenCode === b.chosenCode &&
  a.chosenCatalogItemId === b.chosenCatalogItemId &&
  a.confirmedChangeSha256 === b.confirmedChangeSha256 &&
  a.assistId === b.assistId;

/**
 * The candidate an approval would update: the one staff chose with LINK_AS_UPDATE (saved or drafted), or
 * the single STRONG candidate. `null` when the row would create a new item or has no clear target.
 */
export function updateTarget(
  detail: StaffImportReviewRowDetail,
  drafts: RowDrafts,
): StaffImportCandidate | null {
  const duplicateKey = `DUPLICATE_ACTION:`;
  const saved = latestDecisions(detail.decisions).get(duplicateKey);
  const chosen = drafts[duplicateKey] ?? (saved === undefined ? null : draftOfDecision(saved));
  if (chosen !== null) {
    if (chosen.chosenCode !== "LINK_AS_UPDATE") return null;
    return detail.candidates.find((c) => c.catalogItemId === chosen.chosenCatalogItemId) ?? null;
  }
  const strong = detail.candidates.filter((candidate) => candidate.strength === "STRONG");
  return strong.length === 1 ? (strong[0] ?? null) : null;
}

/** Whether a draft is a complete and valid answer for the decision it belongs to. */
export function draftIsComplete(
  required: StaffImportRequiredDecision,
  draft: DecisionDraft,
  detail: StaffImportReviewRowDetail,
  drafts: RowDrafts,
): boolean {
  const code = draft.chosenCode;
  const optionCodes = required.options.map((option) => option.code);
  switch (required.kind) {
    case "CURRENCY":
      return code !== null && (code === KEEP_AS_TEXT || optionCodes.includes(code));
    case "TIME_ZONE":
      return code !== null && code === required.suggestedCode;
    case "DATE_ORDER":
      return code === "DMY" || code === "MDY";
    case "TAXONOMY":
      return code !== null && optionCodes.includes(code);
    case "DUPLICATE_ACTION":
      if (code === "LINK_AS_UPDATE") {
        return detail.candidates.some((c) => c.catalogItemId === draft.chosenCatalogItemId);
      }
      return (code === "CREATE_NEW" || code === "SKIP") && draft.chosenCatalogItemId === null;
    case "DESTRUCTIVE_UPDATE": {
      const target = updateTarget(detail, drafts);
      return (
        target !== null &&
        target.changeSha256 !== null &&
        draft.chosenCatalogItemId === target.catalogItemId &&
        draft.confirmedChangeSha256 === target.changeSha256
      );
    }
  }
}

export type DecisionState = Readonly<{
  required: StaffImportRequiredDecision;
  key: DecisionKey;
  saved: StaffImportRowDecision | null;
  /** What the form shows: the draft when there is one, else the saved decision, else nothing. */
  shown: DecisionDraft;
  /** The draft differs from the saved decision. */
  changed: boolean;
  /** The shown value is a complete answer. */
  complete: boolean;
}>;

export function decisionStates(
  detail: StaffImportReviewRowDetail,
  drafts: RowDrafts,
): readonly DecisionState[] {
  const latest = latestDecisions(detail.decisions);
  return detail.requiredDecisions.map((required) => {
    const key = decisionKey(required);
    const saved = latest.get(key) ?? null;
    const draft = drafts[key];
    const shown = draft ?? (saved === null ? emptyDraft : draftOfDecision(saved));
    const changed =
      draft !== undefined && (saved === null ? true : !sameDraft(draft, draftOfDecision(saved)));
    return {
      required,
      key,
      saved,
      shown,
      changed,
      complete: draftIsComplete(required, shown, detail, drafts),
    };
  });
}

/** The decisions that can be sent now: changed drafts that are complete. */
export const pendingSaves = (states: readonly DecisionState[]): readonly DecisionState[] =>
  states.filter((state) => state.changed && state.complete);

export function toDecisionBody(
  expectedJobVersion: number,
  state: DecisionState,
): StaffImportRecordDecisionBody {
  const { required, shown } = state;
  const base = { expectedJobVersion, kind: required.kind };
  switch (required.kind) {
    case "CURRENCY":
    case "TIME_ZONE":
    case "DATE_ORDER":
      return { ...base, chosenCode: shown.chosenCode };
    case "TAXONOMY":
      return {
        ...base,
        fieldCode: required.fieldCode,
        chosenCode: shown.chosenCode,
        ...(shown.assistId === null ? {} : { assistId: shown.assistId }),
      };
    case "DUPLICATE_ACTION":
      return {
        ...base,
        chosenCode: shown.chosenCode,
        ...(shown.chosenCode === "LINK_AS_UPDATE"
          ? { chosenCatalogItemId: shown.chosenCatalogItemId }
          : {}),
      };
    case "DESTRUCTIVE_UPDATE":
      return {
        ...base,
        chosenCatalogItemId: shown.chosenCatalogItemId,
        confirmedChangeSha256: shown.confirmedChangeSha256,
      };
  }
}

// --- what a row may do ---------------------------------------------------------------------------------------

const REVIEWABLE: ReadonlySet<string> = new Set(["READY", "WARNING", "DUPLICATE_CANDIDATE"]);

/** A row staff may still approve (after their decisions). An INVALID row may only be skipped. */
export const isReviewable = (outcome: string | null): boolean =>
  outcome !== null && REVIEWABLE.has(outcome);

export type ApprovalAction = "CREATE_NEW" | "UPDATE_EXISTING";

export type RowGate = Readonly<{
  /** The row can be approved now. */
  canApprove: boolean;
  /** The row can be skipped now. */
  canSkip: boolean;
  /** Why it cannot be approved. Empty when it can. */
  reasons: readonly string[];
  /** Only for a possible duplicate. */
  approvalAction: ApprovalAction | null;
  /** Decisions staff chose and did not save yet. */
  unsaved: boolean;
}>;

const reasonFor = (state: DecisionState): string => {
  const kind = decisionKindLabels[state.required.kind];
  return state.required.fieldCode === null
    ? `Decide: ${kind}.`
    : `Decide: ${kind}, ${targetFieldLabel(state.required.fieldCode)}.`;
};

/** What staff may do with a row, given the server's answer and the drafts. */
export function rowGate(detail: StaffImportReviewRowDetail, drafts: RowDrafts): RowGate {
  const { outcome } = detail.row;
  const canSkip = outcome === "INVALID" || (outcome !== null && REVIEWABLE.has(outcome));
  const states = decisionStates(detail, drafts);
  const unsaved = states.some((state) => state.changed);
  if (outcome === null || !REVIEWABLE.has(outcome)) {
    return {
      canApprove: false,
      canSkip,
      reasons:
        outcome === "INVALID"
          ? ["An invalid row cannot be approved. Skip it, or fix the file and import it again."]
          : [],
      approvalAction: null,
      unsaved,
    };
  }
  const reasons: string[] = [];
  for (const state of states) {
    if (state.saved === null || !state.required.resolved) reasons.push(reasonFor(state));
  }
  if (unsaved) reasons.push("Save your decisions first.");

  let approvalAction: ApprovalAction | null = null;
  if (outcome === "DUPLICATE_CANDIDATE") {
    const latest = latestDecisions(detail.decisions).get("DUPLICATE_ACTION:");
    const target = updateTarget(detail, drafts);
    let choseSkip = false;
    if (latest === undefined) {
      // Before the approval the server stores no action. A strong match owes no duplicate decision and
      // updates its one target, as `approvalActionOf` in the use case derives it.
      approvalAction = detail.row.approvalAction ?? (target === null ? null : "UPDATE_EXISTING");
    } else if (latest.chosenCode === "LINK_AS_UPDATE") approvalAction = "UPDATE_EXISTING";
    else if (latest.chosenCode === "CREATE_NEW") approvalAction = "CREATE_NEW";
    else choseSkip = true;
    if (choseSkip) reasons.push("You chose to skip this row. Use Skip row.");
    else if (target?.unchanged === true && approvalAction !== "CREATE_NEW") {
      reasons.push("This update changes nothing. Skip the row.");
    } else if (
      approvalAction === null &&
      !states.some((state) => state.required.kind === "DUPLICATE_ACTION")
    ) {
      // The server owes no duplicate decision and proposes no action: nothing to approve safely.
      reasons.push("Choose what to do with the possible duplicate.");
    }
  }
  const unique = [...new Set(reasons)];
  return { canApprove: unique.length === 0, canSkip, reasons: unique, approvalAction, unsaved };
}
